import type { DropboxOptions, users } from "@dropbox/api-v2-client";
import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import type {
  AuthenticationState,
  AuthenticationStorage,
  DropboxAuthenticationConfiguration,
} from "@mirage/service-auth/types";
import { defaultApiEnvForThisEnv } from "@mirage/service-auth/useApiEnvironment";
import { tagged } from "@mirage/service-logging";
import { getAll as getSettings } from "@mirage/service-settings";
import { nonNil } from "@mirage/shared/util/tiny-utils";
import type { KVStorage } from "@mirage/storage";
import WithDefaults from "@mirage/storage/with-defaults";
import WithOverrides from "@mirage/storage/with-overrides";
import { i18nAutomaticLocale } from "@mirage/translations";
import * as rx from "rxjs";
import * as op from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { ApiEnvironment, AuthenticationStatus } from "../types";

// TODO: access this via ESV-specific service instead of ad-hoc type
interface EsvWindow {
  fenixInitializationComplete?: Promise<unknown> | null;
  controller: {
    getCurrentAccount(): Promise<users.FullAccount>;
    getAccessToken(): Promise<string>;
  };
}

const logger = tagged("service-auth-esv");

/**
 * The main intent is to provide a no-op auth service, since the ESV infrastructure in FSS
 * manages the authentication for ESVs, by automatically appending the user's access token
 * to HTTP requests made by the page.
 *
 * @returns a service that implements the Auth interface
 */
export async function provideEsvAuthService(
  adapter: KVStorage<AuthenticationStorage>,
  config: DropboxAuthenticationConfiguration,
) {
  const esvWindow = window as unknown as EsvWindow;
  const esvController = esvWindow.controller;

  await esvWindow.fenixInitializationComplete;

  const sessionId = uuidv4();
  const overridden = new WithOverrides(adapter, {
    sessionId,
  });

  logger.info("starting auth service");
  const storage = new WithDefaults<AuthenticationStorage>(overridden, {
    // persistent install identifier
    installId: uuidv4(),
    sessionId, // no-op, but required for shape completeness
    // transient code verification for ongoing oauth
    codeVerifier: undefined,
    // authenticated user information
    accessToken: undefined,
    refreshToken: undefined,
    accessTokenExpiresAt: undefined,
    uid: undefined,
    currentAccount: undefined,
    apiEnvironment: defaultApiEnvForThisEnv(),
    forceReauthentication: false,
    shouldTraceRequests: false,
    shouldLogFirstInstall: false,
  });

  /**
   * This method should _not_ be required, since FSS intercepts HTTP requests via
   * `onBeforeSendHeaders, however, requests going out via GRPC Web seem to be
   * somehow bypassing this interception - we need to understand why that's happening.
   * For now, getting the token via the ESV's User Journey Controller is working fine.
   */
  async function getAccessToken() {
    return esvController.getAccessToken();
  }

  async function getCurrentAccount() {
    return esvController.getCurrentAccount();
  }

  const apiEnvObservable = new rx.BehaviorSubject<ApiEnvironment>(
    await storage.get("apiEnvironment"),
  );

  async function getApiEnvironment() {
    return await storage.get("apiEnvironment");
  }

  async function setApiEnvironment(apiEnvironment?: ApiEnvironment | null) {
    if (!apiEnvironment) {
      apiEnvironment = defaultApiEnvForThisEnv();
      logger.debug(
        "setApiEnvironment() - no environment provided, using default:",
        apiEnvironment,
      );
    }

    await storage.set("apiEnvironment", apiEnvironment);
    // and update observable so that any listeners can react to the change
    apiEnvObservable.next(apiEnvironment);

    return apiEnvironment;
  }
  function listenForApiEnvUpdates() {
    return apiEnvObservable.asObservable();
  }

  async function getDefaultApiV2Headers() {
    logger.debug("getDefaultApiV2Headers()");
    const apiEnvironment = await getApiEnvironment();
    const { locale, region } = await getSettings();

    const customHeaders: { [key: string]: string | number } = {};

    if (apiEnvironment === ApiEnvironment.STAGE) {
      customHeaders["X-Dropbox-Backend"] = "stage";
    } else if (apiEnvironment === ApiEnvironment.VALIDATION) {
      customHeaders["X-Dropbox-Backend"] = "stage";
      customHeaders["X-Dropbox-Validation"] = "val";
    }

    if (locale && locale !== i18nAutomaticLocale) {
      customHeaders["Dropbox-API-User-Locale"] = locale;
    }

    if (region) {
      customHeaders["X-Dropbox-Destination"] = region;
    }

    return customHeaders;
  }

  async function getDropboxInitializationParametersOrNull(): Promise<DropboxOptions | null> {
    logger.debug("getDropboxInitializationParametersOrNull()");
    const [accessToken, dropboxAppAuthInitializationParameters] =
      await Promise.all([
        getAccessToken(),
        getDropboxAppAuthInitializationParameters(),
      ]);

    if (!accessToken) {
      logger.debug(
        "getDropboxInitializationParametersOrNull() - access token not found",
      );
      return null;
    }
    return {
      accessToken,
      ...dropboxAppAuthInitializationParameters,
    };
  }

  async function getDropboxInitializationParameters(): Promise<DropboxOptions> {
    return nonNil(
      await getDropboxInitializationParametersOrNull(),
      "access token",
    );
  }

  async function getDropboxAppAuthInitializationParameters(): Promise<DropboxOptions> {
    logger.debug("getDropboxAppAuthInitializationParameters()");
    const shouldTraceRequests = await getShouldTraceRequests();
    const customHeaders = await getDefaultApiV2Headers();

    if (shouldTraceRequests) {
      customHeaders["X-Dropbox-Force-Request-Tracing"] = 1;
    }

    return {
      clientId: config.clientId,
      clientSecret: config.clientSecret,
      domain: config.domain,
      customHeaders,
    };
  }

  async function getShouldTraceRequests() {
    return await storage.get("shouldTraceRequests");
  }

  async function getInstallId(): Promise<string> {
    return storage.get("installId");
  }

  async function getShouldLogFirstInstall(): Promise<boolean> {
    return storage.get("shouldLogFirstInstall");
  }
  async function setShouldLogFirstInstall(value: boolean): Promise<void> {
    return storage.set("shouldLogFirstInstall", value);
  }
  async function getSessionId(): Promise<string> {
    return storage.get("sessionId");
  }

  async function setInstallId(installId: string): Promise<void> {
    return storage.set("installId", installId);
  }

  async function setShouldTraceRequests(shouldTraceRequests: boolean) {
    await storage.set("shouldTraceRequests", shouldTraceRequests);
    return shouldTraceRequests;
  }

  return {
    authServiceImpl: services.provide(
      ServiceId.DASH_AUTH,
      {
        authenticate: () => Promise.resolve(),
        refreshAccessToken: () => Promise.resolve(true),
        checkAndRefreshAccessToken: () => Promise.resolve(true),
        exchangeCodeForToken: () => Promise.resolve(true),
        // @TODO understand ~~~~ WHY ~~~~
        // The ESV infrastructure is supposed to intercept requests and append the
        // authorization header automatically but it does not work for the GRPC Web client.
        // For now we're proxying it via Fenix.
        getAccessToken,
        resetAuthenticationData: () => Promise.resolve(),
        getAuthenticationData: async () => {
          const accessToken = await getAccessToken();
          const accessTokenExpiresAt = Date.now() + 1000 * 60 * 60 * 6;

          return {
            accessToken,
            refreshToken: "--managed-by-fss--",
            accessTokenExpiresAt,
            uid: "-",
          };
        },
        getCurrentAccount,
        getDropboxInitializationParameters,
        getDropboxAppAuthInitializationParameters,
        getInstallId,
        getSessionId,
        getShouldLogFirstInstall,
        getApiEnvironment,
        getShouldTraceRequests,
        setShouldLogFirstInstall,
        /**
         * @TODO We might need to implement a version of this inside the ESV infrastructure, in FSS
         */
        getDefaultApiV2Headers,
        getUpdaterId: () => Promise.resolve(undefined),
        listen: () =>
          rx.of<AuthenticationState>({
            status: AuthenticationStatus.Complete,
          }),
        listenForAccount: () => rx.from(getCurrentAccount()),
        listenForAccountIds: () =>
          rx
            .from(getCurrentAccount())
            .pipe(op.map(({ uid, account_id }) => ({ uid, account_id }))),
        cleanUpStorage: () => Promise.resolve(),
        logout: () => Promise.resolve(),
        setInstallId,
        setApiEnvironment,
        setShouldTraceRequests,
        isFreshLogin: () => Promise.resolve(false),
        setIsFreshLogin: () => Promise.resolve(),
        migrateFromExternalManagement: () => Promise.resolve(),
        getLoginPathWithReturnRedirectURLParam: () => Promise.resolve(""),
        getOauthRedirectURL: () => Promise.resolve(config.oauthRedirectURL),
        listenForApiEnvUpdates,
      },
      [],
    ),
  };
}
